import React, { useState, useMemo, useEffect } from 'react'; // Import useEffect
import { useHistory } from 'react-router-dom';
import ClipLoader from 'react-spinners/ClipLoader';
import CloseIcon from '../icons/close.svg'; // Assuming you have a close icon
import RemoveIcon from '../icons/remove.svg'; // Assuming you have a remove/delete icon
import AddContactIcon from '../icons/add-contact.svg'; // Icon for adding contact
import MessageIcon from '../icons/message.svg'; // Icon for viewing message
import profil1 from '../assets/profil1.jpg'; // Default fallback
import profil2 from '../assets/profil2.png';
import profil3 from '../assets/profil3.png';
import { fetchProfileData, UserProfile } from '../services/profileService.ts'; // Import fetchProfileData and UserProfile type

// Helper to get picture source based on filename (same as in Profile.tsx)
const getPictureSrc = (filename) => {
    switch (filename) {
        case 'profil1.jpg': return profil1;
        case 'profil2.png': return profil2;
        case 'profil3.png': return profil3;
        default: return profil1; // Fallback
    }
};

interface Contact {
    walletAddress: string;
    username: string;
    profilePicture: string;
    isDefault?: boolean; // Optional flag if the username is generated
}

interface ContactListModalProps {
    isOpen: boolean;
    onClose: () => void;
    contacts: Contact[];
    onRemoveContact: (contactAddress: string) => Promise<void>; // Function to handle removal
    onSendMessage: (contact: Contact) => void; // Handler for existing contacts
    isLoading?: boolean; // Optional loading state for contacts list
    unreadCounts: Record<string, number>;
    onAddContactFromAddress: (address: string) => Promise<void>; // New handler to add unknown sender
    onOpenChatWithAddress: (address: string) => void; // New handler to open chat with unknown sender
}

// --- NEW: State for unknown sender profiles ---
type UnknownSenderProfile = Pick<UserProfile, 'username' | 'profilePicture'> | { username: null; profilePicture: null };
// --- END NEW ---

const ContactListModal: React.FC<ContactListModalProps> = ({
    isOpen,
    onClose,
    contacts,
    onRemoveContact,
    onSendMessage, // Keep for existing contacts
    isLoading = false,
    unreadCounts,
    onAddContactFromAddress, // Receive new handler
    onOpenChatWithAddress, // Receive new handler
}) => {
    const history = useHistory();
    const [addingContactAddress, setAddingContactAddress] = useState<string | null>(null); // State for loading indicator
    // --- NEW: State for unknown sender profiles and loading ---
    const [unknownSenderProfiles, setUnknownSenderProfiles] = useState<Record<string, UnknownSenderProfile>>({});
    const [loadingProfiles, setLoadingProfiles] = useState<Record<string, boolean>>({});
    // --- END NEW ---

    // Memoize the calculation of unknown senders
    const unknownSenders = useMemo(() => {
        const contactAddressesLower = contacts.map(c => c.walletAddress.toLowerCase());
        return Object.entries(unreadCounts)
            .filter(([address, count]) => count > 0 && !contactAddressesLower.includes(address.toLowerCase()))
            .map(([address, count]) => ({ address, count }));
    }, [unreadCounts, contacts]);

    // --- NEW: Effect to fetch profiles for unknown senders ---
    useEffect(() => {
        if (isOpen && unknownSenders.length > 0) {
            const fetchProfiles = async () => {
                const profilesToFetch = unknownSenders.filter(sender => !unknownSenderProfiles[sender.address.toLowerCase()] && !loadingProfiles[sender.address.toLowerCase()]);

                if (profilesToFetch.length === 0) return;

                // Mark as loading
                setLoadingProfiles(prev => {
                    const newLoading = { ...prev };
                    profilesToFetch.forEach(sender => { newLoading[sender.address.toLowerCase()] = true; });
                    return newLoading;
                });

                const fetchPromises = profilesToFetch.map(async (sender) => {
                    const addressLower = sender.address.toLowerCase();
                    try {
                        const profile = await fetchProfileData(sender.address);
                        return { address: addressLower, profile: { username: profile?.username || null, profilePicture: profile?.profilePicture || null } };
                    } catch (error) {
                        console.error(`Failed to fetch profile for ${sender.address}:`, error);
                        return { address: addressLower, profile: { username: null, profilePicture: null } }; // Store null on error
                    }
                });

                const results = await Promise.all(fetchPromises);

                // Update profiles and remove from loading
                setUnknownSenderProfiles(prev => {
                    const newProfiles = { ...prev };
                    results.forEach(result => {
                        if (result) {
                            newProfiles[result.address] = result.profile;
                        }
                    });
                    return newProfiles;
                });
                setLoadingProfiles(prev => {
                    const newLoading = { ...prev };
                    results.forEach(result => {
                        if (result) {
                            delete newLoading[result.address];
                        }
                    });
                    return newLoading;
                });
            };
            fetchProfiles();
        }
        // Reset profiles when modal closes? Optional, depends on desired behavior.
        // if (!isOpen) {
        //     setUnknownSenderProfiles({});
        //     setLoadingProfiles({});
        // }
    }, [isOpen, unknownSenders, unknownSenderProfiles, loadingProfiles]); // Dependencies
    // --- END NEW ---

    const handleNavigate = (address: string) => {
        history.push(`/profile?address=${address}`);
        onClose(); // Close modal after navigation
    };

    const handleRemove = async (e: React.MouseEvent, address: string) => {
        e.stopPropagation(); // Prevent navigating when clicking remove
        if (window.confirm(`Are you sure you want to remove this contact?`)) {
            try {
                await onRemoveContact(address);
                // Optional: Add feedback like a toast notification
            } catch (error) {
                console.error("Failed to remove contact:", error);
                // Optional: Show error feedback
            }
        }
    };

    const handleSendMsg = (contact: Contact) => {
        onSendMessage(contact);
    };

    // --- New Handler for Adding Unknown Sender ---
    const handleAddUnknown = async (e: React.MouseEvent, address: string) => {
        e.stopPropagation();
        setAddingContactAddress(address); // Show loading for this specific address
        try {
            await onAddContactFromAddress(address);
            // Success: The contact should disappear from the unknown list automatically
            // when `contacts` prop updates in the parent.
        } catch (error) {
            console.error("Failed to add contact from modal:", error);
            // Optional: Show error feedback
        } finally {
            setAddingContactAddress(null); // Hide loading indicator
        }
    };
    // --- End New Handler ---

    // --- New Handler for Opening Chat with Unknown Sender ---
    const handleOpenChatUnknown = (e: React.MouseEvent, address: string) => {
        e.stopPropagation();
        onOpenChatWithAddress(address);
        // The parent handler (handleOpenChatWithAddress in Profile.tsx) will close the modal.
    };
    // --- End New Handler ---

    if (!isOpen) return null;

    // Helper to truncate address
    const truncateAddress = (addr: string) => `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm"
            onClick={onClose}
        >
            <div
                className="bg-[#1a202c] text-white rounded-lg shadow-xl w-full max-w-md p-6 border border-gray-700 flex flex-col" // Added flex flex-col
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex justify-between items-center mb-4 flex-shrink-0">
                    <h2 className="text-xl font-semibold">My Contacts</h2>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-700">
                        <img src={CloseIcon} alt="Close" className="w-5 h-5" />
                    </button>
                </div>

                {/* Content Area */}
                <div className="flex-grow overflow-y-auto pr-2 space-y-4"> {/* Added space-y-4 */}
                    {isLoading ? (
                        <div className="flex justify-center items-center py-10">
                            <ClipLoader color={'#ffffff'} size={30} />
                        </div>
                    ) : (
                        <>
                            {/* --- Unknown Senders Section --- */}
                            {unknownSenders.length > 0 && (
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-400 mb-2 border-b border-gray-600 pb-1">New Messages</h3>
                                    <ul className="space-y-3">
                                        {unknownSenders.map(({ address, count }) => {
                                            const addressLower = address.toLowerCase();
                                            const profile = unknownSenderProfiles[addressLower];
                                            const isLoadingProfile = loadingProfiles[addressLower];
                                            const displayName = profile?.username || truncateAddress(address);
                                            const pictureSrc = profile?.profilePicture ? getPictureSrc(profile.profilePicture) : null;

                                            return (
                                                <li
                                                    key={address}
                                                    className="flex items-center justify-between p-3 bg-gray-800 rounded-md hover:bg-gray-700 transition-colors"
                                                >
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        {/* --- Updated Picture/Placeholder --- */}
                                                        {pictureSrc ? (
                                                            <img
                                                                src={pictureSrc}
                                                                alt={displayName}
                                                                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                                            />
                                                        ) : (
                                                            <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center flex-shrink-0">
                                                                {isLoadingProfile ? <ClipLoader color={'#ffffff'} size={16} /> : <span className="text-gray-400 text-xl">?</span>}
                                                            </div>
                                                        )}
                                                        {/* --- Updated Display Name --- */}
                                                        <span className={`font-medium truncate ${profile?.username ? '' : 'font-mono text-sm'}`}>
                                                            {displayName}
                                                        </span>
                                                        {isLoadingProfile && !profile?.username && <ClipLoader color={'#ffffff'} size={12} className="ml-1" />}
                                                        {/* --- End Updated Display Name --- */}
                                                    </div>
                                                    <div className="flex items-center gap-2 flex-shrink-0">
                                                        {/* ... existing badge and buttons ... */}
                                                        {/* Unread Badge */}
                                                        <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1">
                                                            {count > 9 ? '9+' : count}
                                                        </span>
                                                        {/* Add Contact Button */}
                                                        <button
                                                            onClick={(e) => handleAddUnknown(e, address)}
                                                            disabled={addingContactAddress === address}
                                                            className="p-1.5 rounded-full hover:bg-blue-800 text-blue-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-wait"
                                                            title="Add Contact"
                                                        >
                                                            {addingContactAddress === address ? (
                                                                <ClipLoader color={'#ffffff'} size={16} />
                                                            ) : (
                                                                <img src={AddContactIcon} alt="Add" className="w-4 h-4" />
                                                            )}
                                                        </button>
                                                        {/* View Message Button */}
                                                        <button
                                                            onClick={(e) => handleOpenChatUnknown(e, address)}
                                                            className="p-1.5 rounded-full hover:bg-purple-800 text-purple-400 hover:text-white transition-colors"
                                                            title="View Messages"
                                                        >
                                                            <img src={MessageIcon} alt="Message" className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}
                            {/* --- End Unknown Senders Section --- */}

                            {/* --- Existing Contacts Section --- */}
                            <div>
                                {unknownSenders.length > 0 && contacts.length > 0 && (
                                     <h3 className="text-lg font-semibold text-gray-400 mb-2 mt-4 border-b border-gray-600 pb-1">Contacts</h3>
                                )}
                                {contacts.length === 0 && unknownSenders.length === 0 ? (
                                    <p className="text-gray-400 text-center py-5">You haven't added any contacts yet.</p>
                                ) : contacts.length > 0 ? (
                                    <ul className="space-y-3">
                                        {contacts.map((contact) => {
                                            // ... (rest of the existing contact mapping logic remains the same) ...
                                            if (!contact || !contact.walletAddress) {
                                                console.error("ContactListModal: Invalid contact object found:", contact);
                                                return null; // Skip rendering this invalid contact
                                            }
                                            const contactAddressLower = contact.walletAddress.toLowerCase();
                                            const unreadCount = unreadCounts[contactAddressLower] || 0;

                                            return (
                                                <li
                                                    key={contact.walletAddress}
                                                    className="flex items-center justify-between p-3 bg-gray-800 rounded-md hover:bg-gray-700 cursor-pointer transition-colors"
                                                    onClick={() => handleNavigate(contact.walletAddress)}
                                                >
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        <img
                                                            src={getPictureSrc(contact.profilePicture)}
                                                            alt={contact.username}
                                                            className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                                        />
                                                        <span className="font-medium truncate">{contact.username}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 flex-shrink-0"> {/* Added flex-shrink-0 */}
                                                        {/* Unread Badge */}
                                                        {unreadCount > 0 && (
                                                            <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1">
                                                                {unreadCount > 9 ? '9+' : unreadCount}
                                                            </span>
                                                        )}
                                                        {/* Send Message Button */}
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleSendMsg(contact);
                                                            }}
                                                            className="p-1.5 rounded-full hover:bg-purple-800 text-purple-400 hover:text-white transition-colors" // Adjusted styling
                                                            title="Send Message"
                                                        >
                                                             <img src={MessageIcon} alt="Message" className="w-4 h-4" /> {/* Use Icon */}
                                                        </button>
                                                        {/* Remove Button */}
                                                        <button
                                                            onClick={(e) => handleRemove(e, contact.walletAddress)}
                                                            className="p-1.5 rounded-full hover:bg-red-800 text-red-400 hover:text-white transition-colors" // Adjusted styling
                                                            title="Remove Contact"
                                                        >
                                                            <img src={RemoveIcon} alt="Remove" className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                ) : null /* No contacts but unknown senders exist */}
                            </div>
                            {/* --- End Existing Contacts Section --- */}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ContactListModal;
